import React, { useEffect, useMemo, useState } from "react";
import { Eye } from "lucide-react";
import { payrollOpsService } from "../../../services/api";
import { formatInr } from "./runPayrollUtils";
import { getCurrentFinancialYear } from "../tax-planning/taxPlanning";
import TdsComputationBreakdownModal from "./TdsComputationBreakdownModal";

function Row({ label, value, highlight = false, darkMode }) {
  return (
    <div
      className={`flex justify-between gap-4 py-1.5 text-sm ${highlight ? "font-semibold" : ""}`}
    >
      <span className={darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]"}>{label}</span>
      <span className="tabular-nums">{value}</span>
    </div>
  );
}

/**
 * Summary card of projected tax / TDS for an employee.
 * Eye opens the same STI-style TDS computation modal used in Run Payroll → Declarations & TDS.
 */
export default function TaxLiabilityIllustration({
  employeeId,
  empCode = null,
  employeeName = null,
  payMonth,
  darkMode = false,
  compact = false,
  showComputationEye = true,
  /** Bump to reload liability from payroll after declaration save/submit. */
  refreshKey = 0,
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showComputation, setShowComputation] = useState(false);

  const month =
    payMonth ||
    `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, "0")}`;

  useEffect(() => {
    if (!employeeId) return undefined;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await payrollOpsService.getTaxLiability(employeeId, month);
        if (!cancelled) setData(res?.data || res);
      } catch (e) {
        if (!cancelled) {
          setError(e?.response?.data?.message || "Unable to load tax illustration");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [employeeId, month, refreshKey]);

  const computationRow = useMemo(() => {
    if (!employeeId) return null;
    const ill = data?.illustration || {};
    return {
      employeeId,
      empCode: empCode || ill.empCode || null,
      name: employeeName || ill.employeeName || null,
      taxRegime: ill.regime || null,
      computedTds: ill.computedTds ?? ill.currentMonthTds,
      actualTds: ill.currentMonthTds,
      annualTaxableIncome: ill.annualTaxableIncome,
      isOverridden: ill.isOverridden,
    };
  }, [employeeId, empCode, employeeName, data]);

  if (loading) {
    return (
      <div
        className={`rounded-lg border p-4 text-sm ${
          darkMode ? "border-[hsl(var(--ds-border))] text-[hsl(var(--ds-muted-fg))]" : "border-[hsl(var(--ds-border))] text-[hsl(var(--ds-muted-fg))]"
        }`}
      >
        Loading tax liability…
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))] p-4 text-sm text-[hsl(var(--ds-fg))]">
        {error}
      </div>
    );
  }

  if (!data?.illustration) return null;

  const ill = data.illustration;
  const breakdown = ill.deductionBreakdown || {};
  const history = data.monthlyHistory || [];

  return (
    <>
      <div
        className={`rounded-lg border ${compact ? "p-3" : "p-4"} ${
          darkMode ? "border-[#3C3C3C] bg-[hsl(var(--ds-surface))]" : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))]"
        }`}
      >
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <div className="flex min-w-0 items-center gap-2">
            <h3 className={`font-semibold ${compact ? "text-sm" : "text-base"}`}>
              Tax liability illustration
            </h3>
            {showComputationEye && computationRow ? (
              <button
                type="button"
                onClick={() => setShowComputation(true)}
                className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-[hsl(var(--ds-border))] text-[hsl(var(--ds-primary))] transition-colors hover:bg-[hsl(var(--ds-muted))]"
                title="View TDS computation breakdown"
                aria-label="View TDS computation breakdown"
              >
                <Eye size={14} />
              </button>
            ) : null}
          </div>
          <span className="rounded-full bg-[hsl(var(--ds-primary)/0.12)] px-2.5 py-0.5 text-xs font-medium text-[hsl(var(--ds-primary))]">
            {ill.regime} regime · FY {ill.financialYear || getCurrentFinancialYear()}
          </span>
        </div>

        <div className={`grid gap-4 ${compact ? "" : "md:grid-cols-2"}`}>
          <div className={`rounded-lg p-3 ${darkMode ? "bg-[hsl(var(--ds-muted))]" : "bg-[hsl(var(--ds-muted))]"}`}>
            <p className="mb-2 text-xs font-medium uppercase tracking-wide opacity-60">
              Income &amp; tax
            </p>
            <Row
              label="Projected annual gross"
              value={formatInr(ill.annualGross)}
              darkMode={darkMode}
            />
            <Row
              label="Total deductions"
              value={formatInr(ill.totalDeductions)}
              darkMode={darkMode}
            />
            {breakdown.standardDeduction != null && (
              <Row
                label="  Standard deduction"
                value={formatInr(breakdown.standardDeduction)}
                darkMode={darkMode}
              />
            )}
            {ill.regime === "OLD" && breakdown.professionalTax > 0 && (
              <Row
                label="  Professional Tax (Sec 16)"
                value={formatInr(breakdown.professionalTax)}
                darkMode={darkMode}
              />
            )}
            {breakdown.hraExemption > 0 && (
              <Row
                label="  HRA exemption"
                value={formatInr(breakdown.hraExemption)}
                darkMode={darkMode}
              />
            )}
            {breakdown.section80C > 0 && (
              <Row
                label="  Section 80C"
                value={formatInr(breakdown.section80C)}
                darkMode={darkMode}
              />
            )}
            {breakdown.section80CFromPayroll > 0 && breakdown.section80C <= 0 && (
              <Row
                label="  Employee PF under 80C"
                value={formatInr(breakdown.section80CFromPayroll)}
                darkMode={darkMode}
              />
            )}
            {Number(breakdown.section80CCD2) > 0 && (
              <Row
                label="  Employer NPS U/s 80CCD(2)"
                value={formatInr(breakdown.section80CCD2)}
                darkMode={darkMode}
              />
            )}
            {breakdown.viaDeductions > 0 && (
              <Row
                label="  VIA deductions"
                value={formatInr(breakdown.viaDeductions)}
                darkMode={darkMode}
              />
            )}
            {breakdown.housePropertySopInterestAllowed > 0 && (
              <Row
                label="  SOP interest allowed (combined)"
                value={formatInr(breakdown.housePropertySopInterestAllowed)}
                darkMode={darkMode}
              />
            )}
            {breakdown.housePropertyLossSetOff > 0 && (
              <Row
                label="  HP loss set-off applied"
                value={formatInr(breakdown.housePropertyLossSetOff)}
                darkMode={darkMode}
              />
            )}
            {breakdown.housePropertyLossCarriedForward > 0 && (
              <Row
                label="  HP loss carried forward"
                value={formatInr(breakdown.housePropertyLossCarriedForward)}
                darkMode={darkMode}
              />
            )}
            {breakdown.housePropertyNetIncome != null &&
              Number(breakdown.housePropertyNetIncome) !== 0 && (
              <Row
                label={
                  Number(breakdown.housePropertyNetIncome) < 0
                    ? "  House property loss (after caps)"
                    : "  House property income"
                }
                value={formatInr(Math.abs(breakdown.housePropertyNetIncome))}
                darkMode={darkMode}
              />
            )}
            {Number(breakdown.housePropertyInterest) > 0 &&
              breakdown.housePropertyNetIncome == null && (
              <Row
                label="  House property interest"
                value={formatInr(breakdown.housePropertyInterest)}
                darkMode={darkMode}
              />
            )}
            <Row
              label="Taxable income (annual)"
              value={formatInr(ill.annualTaxableIncome)}
              highlight
              darkMode={darkMode}
            />
            {Number(ill.taxDetail?.rebate87A) > 0 && (
              <Row
                label="  Rebate U/s 87A"
                value={formatInr(ill.taxDetail.rebate87A)}
                darkMode={darkMode}
              />
            )}
            <Row
              label="Annual tax liability"
              value={formatInr(ill.annualTaxLiability)}
              highlight
              darkMode={darkMode}
            />
          </div>

          <div className={`rounded-lg p-3 ${darkMode ? "bg-[hsl(var(--ds-muted))]" : "bg-[hsl(var(--ds-muted))]"}`}>
            <p className="mb-2 text-xs font-medium uppercase tracking-wide opacity-60">
              TDS this FY
            </p>
            <Row
              label="TDS deducted (YTD)"
              value={formatInr(ill.tdsDeductedYtd)}
              darkMode={darkMode}
            />
            {Number(ill.oneTimeTds) > 0 && (
              <>
                <Row
                  label="Regular month TDS"
                  value={formatInr(ill.regularComputedTds)}
                  darkMode={darkMode}
                />
                <Row
                  label={`One Time tax TDS${
                    ill.oneTimeTaxableAmount
                      ? ` (on ${formatInr(ill.oneTimeTaxableAmount)})`
                      : ""
                  }`}
                  value={formatInr(ill.oneTimeTds)}
                  darkMode={darkMode}
                />
              </>
            )}
            <Row
              label="Current month TDS"
              value={formatInr(ill.currentMonthTds)}
              highlight
              darkMode={darkMode}
            />
            <Row
              label="Balance tax (projected)"
              value={formatInr(ill.projectedBalanceTax)}
              darkMode={darkMode}
            />
            <Row
              label="Remaining FY months"
              value={ill.remainingMonths ?? "—"}
              darkMode={darkMode}
            />
            <Row
              label="Declaration status"
              value={ill.declarationStatus || "—"}
              darkMode={darkMode}
            />
          </div>
        </div>

        {history.length > 0 && !compact && (
          <div className="mt-4">
            <p className="mb-2 text-xs font-medium uppercase tracking-wide opacity-60">
              Monthly TDS history
            </p>
            <div className="overflow-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className={darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]"}>
                    <th className="py-1 text-left">Month</th>
                    <th className="py-1 text-right">Computed</th>
                    <th className="py-1 text-right">Applied</th>
                    <th className="py-1 text-left">Note</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((h) => (
                    <tr
                      key={h.payMonth}
                      className={`border-t ${darkMode ? "border-[#333]" : "border-[hsl(var(--ds-border))]"}`}
                    >
                      <td className="py-1">{h.payMonth}</td>
                      <td className="py-1 text-right tabular-nums">
                        {formatInr(h.computedTds)}
                      </td>
                      <td className="py-1 text-right tabular-nums">
                        {formatInr(h.actualTds)}
                        {h.isOverridden ? " *" : ""}
                      </td>
                      <td className="py-1">{h.isOverridden ? "Override" : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div
          className={`mt-3 flex flex-wrap items-center justify-between gap-2 text-[11px] ${
            darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]"
          }`}
        >
          <p>
            Based on your salary structure, tax regime, and investment declarations. Actual TDS may
            change with salary revisions, one-time inputs, or payroll overrides.
          </p>
          {showComputationEye && computationRow ? (
            <button
              type="button"
              onClick={() => setShowComputation(true)}
              className={`inline-flex items-center gap-1 rounded border px-2 py-1 font-medium ${
                darkMode
                  ? "border-[hsl(var(--ds-border))] text-red-300 hover:bg-[hsl(var(--ds-muted))]"
                  : "border-red-200 text-[hsl(var(--ds-primary))] hover:bg-red-50"
              }`}
            >
              <Eye size={12} />
              Full TDS computation
            </button>
          ) : null}
        </div>
      </div>

      {showComputation && computationRow ? (
        <TdsComputationBreakdownModal
          row={computationRow}
          payMonth={month}
          darkMode={darkMode}
          onClose={() => setShowComputation(false)}
        />
      ) : null}
    </>
  );
}
